// Comps tab, market sources view (docs/decisions/comps-sources.md, "Frontend"):
// the pure rules behind the filter rail, the source cards, the price-over-time
// chart, the record list and the Hi / Lo / Avg table. No React here; the
// components and hooks (components/UnitV2/tabs/CompsSourcesV2*) only render
// what these functions decide.
//
// Sources: `sold` (market sold: dealer sales across the US market, from the market feed), `auction`, `advertised`
// (asking prices) and `own` (the dealer's own sales, from the dealer system).
// Every price is USD: the backend drops everything else before it is served.
// Nothing in this file (or anything it labels) may name the market provider.

export const MARKET_SOURCES = Object.freeze(['sold', 'auction', 'advertised']);
export const ALL_SOURCES = Object.freeze([...MARKET_SOURCES, 'own']);
// The fourth card is a view, not a source.
export const HILO_VIEW = 'hilo';

export const SOURCE_META = Object.freeze({
    sold: {
        label: 'Market sold', noun: 'market sale', nouns: 'market sales', priceWord: 'sold', dateLabel: 'Sold on', shape: 'circle',
        hint: 'Machines sold by dealers across the US market, from the market feed',
    },
    auction: {
        label: 'Auction', noun: 'auction', nouns: 'auctions', priceWord: 'hammer', dateLabel: 'Auction date', shape: 'diamond',
    },
    advertised: {
        label: 'Advertised', noun: 'listing', nouns: 'listings', priceWord: 'asking', dateLabel: 'Listed on', shape: 'ring',
    },
    own: {
        label: 'Your sales', noun: 'sale of yours', nouns: 'sales of yours', priceWord: 'sold', dateLabel: 'Sold on', shape: 'square',
        hint: 'Units your dealership sold, net of trade-in over-allowance',
    },
});

// Below this many records a figure is indicative only (dimmed + said so).
export const SMALL_SAMPLE = 10;
// The record list shows this many, then "Show all N".
export const LIST_PAGE = 10;

export const DATE_RANGES = Object.freeze([
    { value: '90d', label: 'Last 90 days', short: '90 days', days: 90 },
    { value: '6m', label: '6 months', short: '6 months', days: 183 },
    { value: '12m', label: '12 months', short: '12 months', days: 365 },
    { value: 'all', label: 'All', short: 'All dates', days: null },
]);

// 12 months by default. The market feed can lag months behind today: when
// the selected source has nothing in the default window but older records
// exist, the view falls back to All and says so (autoRange below).
export const DEFAULT_RANGE = '12m';
export const ALL_RANGE = 'all';

export const SORT_OPTIONS = Object.freeze([
    { value: 'recent', label: 'Most recent' },
    { value: 'price-desc', label: 'Price high → low' },
    { value: 'price-asc', label: 'Price low → high' },
    { value: 'hours-asc', label: 'Hours low → high' },
]);

export const FOOTNOTE = 'US-dollar results only. Market sold, auction and advertised come from the licensed market feed; '
    + 'your sales come from your dealer system.';

const DAY_MS = 864e5;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Dates ───────────────────────────────────────────────────────────────

/** 'YYYY-MM-DD…' → UTC ms (parsed by parts: no local-midnight drift), or null. */
export function isoToMs(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!m) return null;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isFinite(ms) ? ms : null;
}

/** "Sep 18, 2025" from UTC ms; "—" without a date. */
export function fmtDay(ms) {
    if (ms == null) return '—';
    const d = new Date(ms);
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** "Sep 2025" from UTC ms. */
export function fmtMonth(ms) {
    const d = new Date(ms);
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// ── Money and meters ────────────────────────────────────────────────────

const usd = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const num = new Intl.NumberFormat('en-US');

export const fmtUsd = (v) => (v == null || !Number.isFinite(v) ? '—' : usd.format(v));
export const fmtNum = (v) => (v == null || !Number.isFinite(v) ? '—' : num.format(Math.round(v)));

/** "$410k", "$1.2M" for axis ticks and tight labels. */
export function fmtUsdShort(v) {
    if (v == null || !Number.isFinite(v)) return '—';
    const a = Math.abs(v);
    const sign = v < 0 ? '−' : '';
    if (a >= 999500) {
        const m = Math.round(a / 1e5) / 10;
        return `${sign}$${Number.isInteger(m) ? m : m.toFixed(1)}M`;
    }
    if (a >= 1000) return `${sign}$${Math.round(a / 1000)}k`;
    return `${sign}$${Math.round(a)}`;
}

const METER_LABELS = {
    separator_hours: { label: 'Separator hours', short: 'sep. h', unit: 'hrs' },
    hours: { label: 'Engine hours', short: 'h', unit: 'hrs' },
    miles: { label: 'Miles', short: 'mi', unit: 'mi' },
    kilometers: { label: 'Kilometres', short: 'km', unit: 'km' },
};

/** The meter type most market records carry (ties → hours), or 'hours'. */
export function dominantMeter(records) {
    const counts = {};
    records.forEach((r) => {
        if (r.source === 'own' || !r.meterUnit || !METER_LABELS[r.meterUnit]) return;
        counts[r.meterUnit] = (counts[r.meterUnit] || 0) + 1;
    });
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1] || (a[0] === 'hours' ? -1 : 1))[0];
    return best ? best[0] : 'hours';
}

export const meterLabel = (meter) => (METER_LABELS[meter] || METER_LABELS.hours).label;
export const meterShort = (meter) => (METER_LABELS[meter] || METER_LABELS.hours).short;
export const meterUnitWord = (meter) => (METER_LABELS[meter] || METER_LABELS.hours).unit;

/** The subject unit's own reading on that meter (separator meter for combines). */
export function unitMeterValue(unit, meter) {
    const pos = (v) => (typeof v === 'number' && v > 0 ? v : null);
    if (meter === 'separator_hours') return pos(unit?.Sep_Meter);
    return pos(unit?.ODOMETER);
}

export const unitAsk = (unit) => (typeof unit?.Best_Sell_Price === 'number' && unit.Best_Sell_Price > 0 ? unit.Best_Sell_Price : null);
export function unitEstimate(unit) {
    const v = unit?.PredictedPrice || unit?.MLPrediction || null;
    return typeof v === 'number' && v > 0 ? v : null;
}
export function unitYear(unit) {
    const y = Number(unit?.YEAR_MANUF || unit?.Model_Year);
    return Number.isInteger(y) && y > 1900 ? y : null;
}

// ── Normalising the payloads ────────────────────────────────────────────

const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * One market source payload → records in one shape. `idx` is the record's
 * position in the served `records` (the advertised photo index); `id` is
 * unique across sources.
 */
export function normalizeSource(payload, source) {
    const rows = Array.isArray(payload?.records) ? payload.records : [];
    return rows.map((r, idx) => ({
        id: `${source}:${idx}`,
        source,
        idx,
        year: Number.isInteger(r.year) ? r.year : null,
        price: finite(r.price) != null && r.price > 0 ? r.price : null,
        t: isoToMs(r.date),
        hours: finite(r.hours),
        meterUnit: text(r.meter_unit),
        state: text(r.state),
        region: text(r.region),
        city: text(r.city),
        options: text(r.options_text),
        photoCount: Number.isInteger(r.photo_count) ? r.photo_count : 0,
        stock: null,
        branch: null,
    }));
}

/** The own-sales payload → the same record shape (engine hours, no region). */
export function normalizeOwn(payload) {
    const rows = Array.isArray(payload?.sales) ? payload.sales : [];
    return rows.map((s, idx) => ({
        id: `own:${idx}`,
        source: 'own',
        idx,
        year: Number.isInteger(s.year) ? s.year : null,
        price: finite(s.sale_price) != null && s.sale_price > 0 ? s.sale_price : null,
        t: isoToMs(s.sale_date),
        hours: finite(s.hours),
        meterUnit: 'hours',
        state: null,
        region: null,
        city: null,
        options: null,
        photoCount: 0,
        stock: s.stock_number != null ? String(s.stock_number) : null,
        branch: text(s.branch),
    }));
}

/** Region key of a record: its region, else its state, else null. */
export const regionOf = (r) => r.region || r.state || null;

// ── Filters ─────────────────────────────────────────────────────────────

export const EMPTY_FILTERS = Object.freeze({
    range: DEFAULT_RANGE, hMin: null, hMax: null, years: null, regions: null,
});

const rangeDays = (range) => DATE_RANGES.find((r) => r.value === range)?.days ?? null;

/** Earliest date a record may have under `range` (UTC ms), or null for All. */
export function rangeStart(range, now) {
    const days = rangeDays(range);
    return days == null ? null : now - days * DAY_MS;
}

/**
 * Does one record pass one filter dimension? Region applies to market
 * records only (your own sales have no market region), hours only when the
 * record reads the same meter as the filter (your sales carry engine hours,
 * a combine's market records separator hours).
 */
const PASSES = {
    range: (r, f, ctx) => {
        const start = rangeStart(f.range, ctx.now);
        return start == null || (r.t != null && r.t >= start);
    },
    hours: (r, f, ctx) => {
        if (f.hMin == null && f.hMax == null) return true;
        if (r.source === 'own' && ctx.meter !== 'hours') return true;
        if (r.hours == null) return false;
        if (f.hMin != null && r.hours < f.hMin) return false;
        if (f.hMax != null && r.hours > f.hMax) return false;
        return true;
    },
    years: (r, f) => f.years == null || (r.year != null && f.years.includes(r.year)),
    regions: (r, f) => {
        if (f.regions == null || r.source === 'own') return true;
        const key = regionOf(r);
        return key != null && f.regions.includes(key);
    },
};
export const FILTER_KEYS = Object.freeze(Object.keys(PASSES));

/** Which dimensions of `f` differ from the defaults (chips, "Reset all"). */
export function activeFilterKeys(f) {
    return FILTER_KEYS.filter((k) => {
        if (k === 'range') return f.range !== DEFAULT_RANGE;
        if (k === 'hours') return f.hMin != null || f.hMax != null;
        return f[k] != null;
    });
}

/** Which dimensions of `f` remove anything (the default 12 months does). */
export function narrowingKeys(f) {
    return FILTER_KEYS.filter((k) => {
        if (k === 'range') return f.range !== ALL_RANGE;
        if (k === 'hours') return f.hMin != null || f.hMax != null;
        return f[k] != null;
    });
}

/** Records passing every filter (optionally all but `skip`). */
export function applyFilters(records, f, ctx, skip = null) {
    const keys = narrowingKeys(f).filter((k) => k !== skip);
    if (keys.length === 0) return records;
    return records.filter((r) => keys.every((k) => PASSES[k](r, f, ctx)));
}

/** `f` with one dimension back to its default. */
export function relaxFilter(f, key) {
    if (key === 'range') return { ...f, range: ALL_RANGE };
    if (key === 'hours') return { ...f, hMin: null, hMax: null };
    return { ...f, [key]: null };
}

export const FILTER_LABELS = Object.freeze({
    range: 'date range', hours: 'hours range', years: 'model years', regions: 'regions',
});

/**
 * Zero results: the one filter whose removal brings back the most records
 * (ties → the order of FILTER_KEYS). Null when nothing is filtered or no
 * single relax helps.
 */
export function zeroResultCulprit(records, f, ctx) {
    let best = null;
    narrowingKeys(f).forEach((key) => {
        const gain = applyFilters(records, f, ctx, key).length;
        if (gain > 0 && (!best || gain > best.gain)) best = { key, label: FILTER_LABELS[key], gain };
    });
    return best;
}

/** The applied-filter summary chips: [{ key, label }]. */
export function filterChips(f, meter) {
    const chips = [];
    if (f.range !== DEFAULT_RANGE) chips.push({ key: 'range', label: DATE_RANGES.find((r) => r.value === f.range)?.short || f.range });
    if (f.hMin != null || f.hMax != null) {
        const w = meterUnitWord(meter);
        let label;
        if (f.hMin != null && f.hMax != null) label = `${fmtNum(f.hMin)}–${fmtNum(f.hMax)} ${w}`;
        else if (f.hMin != null) label = `≥ ${fmtNum(f.hMin)} ${w}`;
        else label = `≤ ${fmtNum(f.hMax)} ${w}`;
        chips.push({ key: 'hours', label });
    }
    if (f.years != null) chips.push({ key: 'years', label: f.years.length ? [...f.years].sort().join(', ') : 'No years' });
    if (f.regions != null) chips.push({ key: 'regions', label: f.regions.length ? f.regions.join(', ') : 'No regions' });
    return chips;
}

/** Toggle a value in a list filter; null = every value (all on). */
export function toggleInList(list, value, allValues) {
    const current = list == null ? [...allValues] : list;
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    const isAll = allValues.length > 0 && allValues.every((v) => next.includes(v));
    return isAll ? null : next.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

// ── Statistics ──────────────────────────────────────────────────────────

/** Linear-interpolation quantile of an ASCENDING array (the backend's rule). */
export function quantile(sorted, q) {
    if (!sorted.length) return null;
    const pos = (sorted.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** n, median, mean, min, max, p25, p75 over the priced records. */
export function priceStats(records) {
    const prices = records.map((r) => r.price).filter((p) => p != null).sort((a, b) => a - b);
    const n = prices.length;
    if (!n) {
        return {
            n: 0, count: records.length, median: null, mean: null, min: null, max: null, p25: null, p75: null,
        };
    }
    return {
        n,
        count: records.length,
        median: quantile(prices, 0.5),
        mean: prices.reduce((s, p) => s + p, 0) / n,
        min: prices[0],
        max: prices[n - 1],
        p25: quantile(prices, 0.25),
        p75: quantile(prices, 0.75),
    };
}

/** Newest record date (UTC ms) or null. */
export const newestDate = (records) => records.reduce((m, r) => (r.t != null && (m == null || r.t > m) ? r.t : m), null);

/** Share (0–100, rounded) of priced records strictly below `value`. */
export function shareBelow(records, value) {
    const prices = records.map((r) => r.price).filter((p) => p != null);
    if (!prices.length || value == null) return null;
    return Math.round((prices.filter((p) => p < value).length / prices.length) * 100);
}

// ── Chart geometry ──────────────────────────────────────────────────────

/**
 * Robust y-domain: about P2–P98 of the visible prices, always widened to
 * include the ask and the estimate, padded 6 %. Prices outside it are drawn
 * pinned to the edge and counted ("+N above range").
 */
export function robustDomain(prices, include = []) {
    const sorted = prices.filter((p) => p != null && Number.isFinite(p)).sort((a, b) => a - b);
    const extra = include.filter((v) => v != null && Number.isFinite(v));
    if (!sorted.length && !extra.length) return null;
    let lo;
    let hi;
    if (sorted.length >= 20) {
        lo = quantile(sorted, 0.02);
        hi = quantile(sorted, 0.98);
    } else if (sorted.length) {
        [lo] = sorted;
        hi = sorted[sorted.length - 1];
    }
    extra.forEach((v) => {
        lo = lo == null ? v : Math.min(lo, v);
        hi = hi == null ? v : Math.max(hi, v);
    });
    const span = hi - lo || Math.abs(hi) * 0.1 || 1;
    return [Math.max(0, lo - span * 0.06), hi + span * 0.06];
}

/** Records split by the domain: inside, above, below. */
export function clipToDomain(records, [lo, hi]) {
    const out = { inside: [], above: [], below: [] };
    records.forEach((r) => {
        if (r.price == null) return;
        if (r.price > hi) out.above.push(r);
        else if (r.price < lo) out.below.push(r);
        else out.inside.push(r);
    });
    return out;
}

/**
 * The chart's time window. A date range is a true, fixed window up to today
 * (empty months stay visible). All is fitted to the records actually shown,
 * oldest to newest, padded a little, so data that ends months ago does not
 * sit in one narrow strip.
 */
export function timeDomain(range, now, records) {
    const start = rangeStart(range, now);
    if (start != null) return [start, now];
    let oldest = null;
    let newest = null;
    records.forEach((r) => {
        if (r.t == null) return;
        if (oldest == null || r.t < oldest) oldest = r.t;
        if (newest == null || r.t > newest) newest = r.t;
    });
    if (oldest == null) return [now - 365 * DAY_MS, now];
    const padMs = Math.max(7 * DAY_MS, (newest - oldest) * 0.03);
    return [oldest - padMs, newest + padMs];
}

/**
 * The window holds nothing of this source, but older records pass every
 * other filter: { newest } (UTC ms of the newest such record), else null.
 */
export function olderThanWindow(records, f, ctx) {
    if (f.range === ALL_RANGE || applyFilters(records, f, ctx).length > 0) return null;
    const older = applyFilters(records, f, ctx, 'range');
    return older.length ? { newest: newestDate(older), count: older.length } : null;
}

/**
 * The default window holds none of the selected source's records but older
 * ones exist: fall back to All. Decided on the date alone (the other filters
 * never take part, so narrowing hours or years can never flip the range),
 * and only while the user has not picked a range. The caller applies it
 * once and keeps it (useCompsView.applyAutoRange), so it cannot loop.
 */
export function autoRange(records, f, ctx, rangeChosen) {
    if (rangeChosen || f.range !== DEFAULT_RANGE || !records.length) return null;
    const inWindow = applyFilters(records, { ...EMPTY_FILTERS, range: f.range }, ctx);
    return inWindow.length === 0 ? ALL_RANGE : null;
}

/**
 * Chip counts for one filter dimension: records of the selected source that
 * pass every OTHER filter, grouped by `keyOf`. Map key → count.
 */
export function dimensionCounts(records, f, ctx, dim, keyOf) {
    const counts = new Map();
    applyFilters(records, f, ctx, dim).forEach((r) => {
        const k = keyOf(r);
        if (k != null) counts.set(k, (counts.get(k) || 0) + 1);
    });
    return counts;
}

/** Month-start ticks in [t0, t1], thinned to at most `max`. */
export function monthTicks(t0, t1, max = 7) {
    const d0 = new Date(t0);
    let y = d0.getUTCFullYear();
    let m = d0.getUTCMonth() + (d0.getUTCDate() > 1 ? 1 : 0);
    const all = [];
    for (;;) {
        if (m > 11) { y += 1; m -= 12; }
        const t = Date.UTC(y, m, 1);
        if (t > t1) break;
        all.push(t);
        m += 1;
    }
    if (all.length <= max) return all;
    const step = [2, 3, 6, 12].find((s) => Math.ceil(all.length / s) <= max) || 12;
    // Quarter / half-year / year steps sit on calendar months (Jan, Apr, Jul…).
    if (step >= 3) return all.filter((t) => new Date(t).getUTCMonth() % step === 0);
    return all.filter((_, i) => i % step === 0);
}

// ── Words ───────────────────────────────────────────────────────────────

/**
 * "−$48,400 vs your ask" / "+$8,000 vs your ask" / "Same as your ask"; null
 * without both. Signed and neutral: a comp above the ask is not "bad" (it
 * may mean the unit is underpriced), so no colour judges it.
 */
export function deltaVsAsk(price, ask) {
    if (price == null || ask == null) return null;
    const d = Math.round(price - ask);
    if (d === 0) return { text: 'Same as your ask', tone: 'same' };
    return { text: `${d < 0 ? '−' : '+'}${fmtUsd(Math.abs(d))} vs your ask`, tone: d < 0 ? 'below' : 'above' };
}

/** "+1 yr · +900 h vs this unit" (or null when nothing compares). */
export function unitDeltas(record, { year, meterValue, meter }) {
    const parts = [];
    if (record.year != null && year != null) {
        const d = record.year - year;
        parts.push(d === 0 ? 'same year' : `${d > 0 ? '+' : '−'}${Math.abs(d)} yr`);
    }
    const sameMeter = record.source === 'own' ? meter === 'hours' : (record.meterUnit || 'hours') === meter;
    if (record.hours != null && meterValue != null && sameMeter) {
        const d = Math.round(record.hours - meterValue);
        parts.push(d === 0 ? 'same hours' : `${d > 0 ? '+' : '−'}${fmtNum(Math.abs(d))} ${meterShort(meter)}`);
    }
    return parts.length ? `${parts.join(' · ')} vs this unit` : null;
}

/** "Dubuque, IA" / "IA" / the region / null. Upper-case city names are softened. */
export function placeOf(r) {
    if (r.source === 'own') return r.branch ? `${r.branch} branch` : null;
    const city = r.city ? r.city.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : null;
    if (city && r.state) return `${city}, ${r.state}`;
    return r.state || r.region || city;
}

const VERDICTS = {
    above: { chip: 'Above market', tone: 'warn' },
    inline: { chip: 'In line', tone: 'ok' },
    below: { chip: 'Below market', tone: 'below' },
};

/**
 * The line under the chart: where the ask sits in the reference source's
 * filtered prices (above the 75th percentile = above market, below the
 * 25th = below), and what your own sales of the model fetched.
 */
export function insight({ ask, refSource, refRecords, ownRecords }) {
    const meta = SOURCE_META[refSource] || SOURCE_META.sold;
    const stats = priceStats(refRecords);
    const own = priceStats(ownRecords || []);
    let verdict = null;
    let sentence = null;
    const basis = `median ${fmtUsd(stats.median)} over ${stats.n}`;
    if (stats.n > 0 && stats.n < SMALL_SAMPLE) {
        // Too few to call it: no badge, just the facts.
        sentence = `Too few ${meta.nouns} to judge: only ${stats.n} in this view (${basis}).`;
    } else if (ask != null && stats.n > 0) {
        let kind = 'inline';
        if (ask > stats.p75) kind = 'above';
        else if (ask < stats.p25) kind = 'below';
        verdict = { kind, ...VERDICTS[kind] };
        const below = refRecords.filter((r) => r.price != null && r.price < ask).length;
        const share = (below / stats.n) * 100;
        // Never "0%" or "100%": the true ends say so in words, and a share
        // that would round to them (one of 201) says "almost".
        if (below === 0) sentence = `Your ask is below every one of the ${meta.nouns} in this view (${basis}).`;
        else if (below === stats.n) sentence = `Your ask is above every one of the ${meta.nouns} in this view (${basis}).`;
        else if (share < 1) sentence = `Your ask is below almost every one of the ${meta.nouns} in this view (${basis}).`;
        else if (share > 99) sentence = `Your ask is above almost every one of the ${meta.nouns} in this view (${basis}).`;
        else sentence = `Your ask is higher than ${Math.round(share)}% of ${meta.nouns} in this view (${basis}).`;
    } else if (stats.n > 0) {
        sentence = `${stats.n} ${stats.n === 1 ? meta.noun : meta.nouns} in this view, median ${fmtUsd(stats.median)}. No asking price set on this unit.`;
    } else {
        sentence = `No ${meta.nouns} in this view.`;
    }
    const ownSentence = own.n > 0
        ? `Your own sales of this model: median ${fmtUsd(own.median)} over ${own.n} sale${own.n === 1 ? '' : 's'}.`
        : null;
    return {
        verdict, sentence, ownSentence, small: stats.n > 0 && stats.n < SMALL_SAMPLE,
    };
}

// ── Sorting ─────────────────────────────────────────────────────────────

/** Nulls last in every mode. */
export function sortRecords(records, mode) {
    const key = { recent: 't', 'price-desc': 'price', 'price-asc': 'price', 'hours-asc': 'hours' }[mode] || 't';
    const dir = mode === 'price-asc' || mode === 'hours-asc' ? 1 : -1;
    return [...records].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av == null && bv == null) return a.idx - b.idx;
        if (av == null) return 1;
        if (bv == null) return -1;
        return (av - bv) * dir || a.idx - b.idx;
    });
}

// ── Address (shareable filters) ─────────────────────────────────────────
//
//   cs = sold | auction | advertised | hilo   (selected card; default sold)
//   cr = 90d | 6m | all                       (date range; default 12m)
//   ch = <min>-<max>                          (hours; either side may be empty)
//   cy = 2021.2022                            (model years; '-' = none)
//   cg = <region>~<region>                    (regions; '-' = none)
//   co = price-desc | price-asc | hours-asc   (list sort; default recent)

export const URL_KEYS = Object.freeze(['cs', 'cr', 'ch', 'cy', 'cg', 'co']);
const VIEWS = [...MARKET_SOURCES, HILO_VIEW];

const parseList = (raw, map) => {
    if (raw == null) return null;
    if (raw === '-') return [];
    const out = raw.split(/[.~]/).map(map).filter((v) => v != null && v !== '');
    return out.length ? [...new Set(out)] : null;
};

/** The comps view state in a query string (unknown values ignored). */
export function parseCompsUrl(search) {
    let p;
    try { p = new URLSearchParams(search || ''); } catch (e) { p = new URLSearchParams(); }
    const view = VIEWS.includes(p.get('cs')) ? p.get('cs') : 'sold';
    const rangeChosen = DATE_RANGES.some((r) => r.value === p.get('cr'));
    const range = rangeChosen ? p.get('cr') : DEFAULT_RANGE;
    let hMin = null;
    let hMax = null;
    const h = /^(\d*)-(\d*)$/.exec(p.get('ch') || '');
    if (h) {
        hMin = h[1] ? Number(h[1]) : null;
        hMax = h[2] ? Number(h[2]) : null;
    }
    const years = parseList(p.get('cy'), (s) => (/^\d{4}$/.test(s) ? Number(s) : null));
    let regions = null;
    if (p.has('cg')) {
        const raw = p.get('cg');
        const list = raw === '-' ? [] : [...new Set(raw.split('~').filter(Boolean))];
        regions = raw === '-' || list.length ? list : null;
    }
    const sort = SORT_OPTIONS.some((o) => o.value === p.get('co')) ? p.get('co') : 'recent';
    return {
        view, sort, rangeChosen, filters: { range, hMin, hMax, years, regions },
    };
}

/**
 * `search` with the comps params rewritten (other params, e.g. tab, kept).
 * `rangeChosen`: the user picked the range, so `cr` is written even for the
 * default 12 months (else a reload would let the automatic fallback replace
 * it). The automatic fallback itself is passed as the default, unchosen.
 */
export function serializeCompsUrl(search, {
    view, sort, filters: f, rangeChosen = false,
}) {
    let p;
    try { p = new URLSearchParams(search || ''); } catch (e) { p = new URLSearchParams(); }
    URL_KEYS.forEach((k) => p.delete(k));
    if (view && view !== 'sold') p.set('cs', view);
    if (f.range !== DEFAULT_RANGE || rangeChosen) p.set('cr', f.range);
    if (f.hMin != null || f.hMax != null) p.set('ch', `${f.hMin ?? ''}-${f.hMax ?? ''}`);
    if (f.years != null) p.set('cy', f.years.length ? [...f.years].sort().join('.') : '-');
    if (f.regions != null) p.set('cg', f.regions.length ? f.regions.join('~') : '-');
    if (sort && sort !== 'recent') p.set('co', sort);
    const s = p.toString();
    return s ? `?${s}` : '';
}

// ── Empty / unavailable wording ─────────────────────────────────────────

const yearsText = (years) => {
    if (!Array.isArray(years) || !years.length) return null;
    const lo = Math.min(...years);
    const hi = Math.max(...years);
    return lo === hi ? `${lo}` : `${lo}–${hi}`;
};

/** The message of a source that has nothing to show (status empty / not_covered / unavailable). */
export function sourceEmptyText(source, payload) {
    const meta = SOURCE_META[source];
    const years = yearsText(payload?.window?.years);
    const status = payload?.status;
    if (source === 'own') {
        if (payload?.available === false) return 'Your sales cannot be matched: this unit lacks a make, model or year.';
        return `No sales of yours for this model${years ? ` (model years ${years})` : ''} in the last 3 years.`;
    }
    if (status === 'not_covered') return `This model is not in the market feed, so there are no ${meta.nouns} to compare.`;
    if (status === 'unavailable') return `${meta.label} data is unavailable right now.`;
    return `No ${meta.nouns} for this model${years ? ` (model years ${years})` : ''} in the market feed.`;
}

/**
 * The one-panel state when no market source has anything for the unit:
 * true once all three answered `empty` / `not_covered` (none loading or failed).
 */
export function noMarketData(payloads) {
    return MARKET_SOURCES.every((k) => {
        const st = payloads[k]?.status;
        return st === 'empty' || st === 'not_covered';
    });
}

/** Why there is no market data, in plain words. */
export function noMarketReason(payloads, unit) {
    const covered = MARKET_SOURCES.map((k) => payloads[k]).find((p) => p?.status === 'not_covered');
    const raw = [unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ').trim() || 'this unit';
    const make = String(unit?.MAKE || '').trim() || 'this make';
    const years = yearsText(MARKET_SOURCES.map((k) => payloads[k]?.window?.years).find((y) => y?.length));
    switch (covered?.reason) {
    case 'model_not_matched': return `We couldn't match ${raw} to the market catalogue.`;
    case 'make_not_covered': return `The market feed does not cover ${make} yet.`;
    case 'category_not_covered': return 'The market feed does not cover this kind of equipment yet.';
    case 'insufficient_data': return 'This unit has no model year, so it cannot be matched to the market.';
    default: return `The market feed has no sales, auctions or listings of ${raw}${years ? ` for model years ${years}` : ''}.`;
    }
}

/** "Updated Oct 21, 2026" for a stale payload. */
export const updatedText = (asOf) => {
    const ms = isoToMs(asOf);
    return ms == null ? 'Updating' : `Updated ${fmtDay(ms)} · refreshing`;
};
